import { useEffect, useState } from 'react'
import { X, Loader2, Store } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { useStore, type Store as StoreType, type CartItem } from '../store/useStore'
import { useI18n } from '../i18n/useI18n'
import { api } from '../services/api'
import { showToast } from './Toast'
import { getOrCreateGuestId } from '../lib/guestId'
import { itemNameLines } from '../lib/itemName'
import { toOrderItems } from '../lib/cartPayload'
import { cartLineOf } from '../store/useStore'
import VariationLine from './VariationLine'
import { useCartRejectionRecovery } from '../hooks/useCartRejectionRecovery'

interface Props {
  open: boolean
  store: StoreType
  items: CartItem[]
  onClose: () => void
  /** #2031 選択肢が変わって弾かれたとき、メニューを取り直す。 */
  onMenuStale?: () => void | Promise<void>
}

/**
 * #1402 客がスマホで選んだ注文を、レジで会計してもらうための受付番号。
 *
 * 店員が口頭で聞いて入力する代わりに、客が自分で多言語メニューから選ぶ。
 * 聞き間違いが構造的に消え、内容が違えば客自身の入力ミスになる。訪日客と
 * 日本語でやり取りしなくて済むのも大きい (券売機が日本中にある理由と同じ)。
 *
 * **注文はまだ作らない。** レジで会計が通った時点で注文化される (#1217 の
 * 「決済が通ってから注文を作る」をそのまま使う) ので、レジまで来なかった客の
 * 分は厨房にも伝票にも集計にも出ない。
 *
 * QR と 4 桁の番号を両方出す — スキャナ不調やカメラ不許可のとき、番号を口頭で
 * 伝えれば通る逃げ道を残しておく。
 */
export default function CounterRequestSheet({ open, store, items, onClose, onMenuStale }: Props) {
  const { t } = useI18n()
  const recoverRejectedCart = useCartRejectionRecovery(items, onMenuStale)
  const { clearCart, userId } = useStore()
  const [issuing, setIssuing] = useState(false)
  const [issued, setIssued] = useState<{ code: string; totalAmount: number; expiresInMinutes: number } | null>(null)

  useEffect(() => {
    if (!open) setIssued(null)
  }, [open])

  if (!open) return null

  const total = items.reduce((n, i) => n + i.price * i.quantity, 0)

  const issue = async () => {
    if (!items.length || issuing) return
    // #1541 ゲスト ID は**発行済みのときだけ**ヘッダに載る仕組み (単に見ている
    // だけの客に ID を配らないため)。発行の責任は呼び出し側にあり、ここが
    // 抜けていたので「卓注文をしていないゲストがいきなりレジ払いを選ぶ」と
    // 401 Login or guest id required で弾かれていた。レジ払いは**ログイン不要**が
    // 売りの導線 (#1402) なので、CartSheet と同じ規約に揃える。
    if (!userId) getOrCreateGuestId()
    setIssuing(true)
    try {
      const res = await api.createCounterRequest(
        store.id,
        toOrderItems(items),
      )
      setIssued(res)
      // カートは空にする。番号を出した後にカートを触られると、レジに出る内容と
      // 客の画面がズレる (レジ側は発行時点の内容で確定している)。
      clearCart()
    } catch (e) {
      if (await recoverRejectedCart(e)) { onClose(); return }
      showToast({ type: 'error', message: t('counterRequest.failed') })
    } finally {
      setIssuing(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center bg-black/50 animate-fade-in" onClick={onClose}>
      <div
        className="relative w-full max-w-[430px] rounded-t-2xl bg-white p-6 pb-8 animate-slide-up max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 tap-effect"
          aria-label="close"
        >
          <X size={18} className="text-text-secondary" />
        </button>

        {!issued ? (
          <>
            <div className="mb-1 flex items-center gap-2">
              <Store size={18} className="text-navy" />
              <h3 className="font-display text-lg font-semibold text-text-primary">
                {t('counterRequest.title')}
              </h3>
            </div>
            <p className="mb-4 font-body text-xs text-text-tertiary">{store.name}</p>

            <ul className="mb-4 space-y-1.5">
              {items.map((i) => (
                <li key={cartLineOf(i)} className="flex justify-between font-body text-xs">
                  {/* #1857 客の言語が主・日本語が従。カウンターで店員に見せて確認できる */}
                  <span className="min-w-0 flex-1 text-text-secondary">
                    <span className="block">
                      {itemNameLines(i).primary}
                      {i.quantity > 1 && <span className="ml-1 text-text-tertiary">×{i.quantity}</span>}
                    </span>
                    {itemNameLines(i).secondary && (
                      <span className="block font-jp text-[11px] text-text-tertiary">
                        {itemNameLines(i).secondary}
                      </span>
                    )}
                    <VariationLine item={i} />
                  </span>
                  <span className="text-text-primary">¥{(i.price * i.quantity).toLocaleString()}</span>
                </li>
              ))}
            </ul>

            <div className="mb-4 flex items-center justify-between rounded-xl border border-gray-100 p-3">
              <span className="font-body text-sm text-text-secondary">{t('receipt.total')}</span>
              <span className="font-display text-xl font-bold text-text-primary">
                ¥{total.toLocaleString()}
              </span>
            </div>

            <p className="mb-4 rounded-lg bg-navy/5 px-3 py-2 font-body text-[11px] leading-relaxed text-text-secondary">
              {t('counterRequest.hint')}
            </p>

            <button
              onClick={() => void issue()}
              disabled={issuing || !items.length}
              className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-navy font-display text-base font-semibold text-white tap-effect disabled:bg-gray-300"
            >
              {issuing ? <Loader2 size={18} className="animate-spin" /> : t('counterRequest.issueButton')}
            </button>
          </>
        ) : (
          <>
            <h3 className="mb-1 text-center font-display text-lg font-semibold text-text-primary">
              {t('counterRequest.issuedTitle')}
            </h3>
            <p className="mb-4 text-center font-body text-xs text-text-tertiary">
              {t('counterRequest.issuedHint')}
            </p>

            <div className="mb-4 flex flex-col items-center rounded-2xl border-2 border-navy/20 bg-navy/5 py-5">
              <p className="font-body text-xs text-text-secondary">{t('counterRequest.codeLabel')}</p>
              <p className="font-display text-5xl font-bold tracking-[0.2em] text-navy">
                {issued.code}
              </p>
              <div className="mt-3 rounded-xl bg-white p-2">
                <QRCodeSVG value={issued.code} size={132} />
              </div>
            </div>

            <div className="mb-4 flex items-center justify-between rounded-xl border border-gray-100 p-3">
              <span className="font-body text-sm text-text-secondary">{t('receipt.total')}</span>
              <span className="font-display text-xl font-bold text-text-primary">
                ¥{issued.totalAmount.toLocaleString()}
              </span>
            </div>

            <p className="text-center font-body text-[11px] text-text-tertiary">
              {t('counterRequest.expiry', { minutes: String(issued.expiresInMinutes) })}
            </p>
          </>
        )}
      </div>
    </div>
  )
}
